import React from "react";
import {
  ArrayInput,
  AutocompleteInput,
  CreateProps,
  FormDataConsumer,
  ReferenceInput,
  required,
  SimpleForm,
  SimpleFormIterator,
} from "react-admin";
import { GetParam, SchoolIdSelected } from "utils/tools";
import { Grid, Typography } from "@material-ui/core";
import CustomCreateToolbar from "../../components/CustomCreateToolbar";
import SanitizedGrid from "../../components/SanitizedGrid";
import SelectedSchoolInput from "../../components/SelectedSchoolInput";
import CustomWaveInput from "../../components/CustomWaveInput";
import CustomRouteInput from "../../components/CustomRouteInput";
import CreateWrapper from "../../components/CreateWrapper";
import RoutePriceComponent from "./components/RoutePriceComponent";
import CustomNumberInput from "../../components/CustomNumberInput";
import Alert from "@material-ui/lab/Alert";

// Custom validator to check for unique routeIds
const validateUniqueRouteId = (subcontractorAdjustmentPrices: any[]) => {
  if (
    !subcontractorAdjustmentPrices ||
    subcontractorAdjustmentPrices.length === 0 ||
    subcontractorAdjustmentPrices.includes(undefined)
  ) {
    return undefined; // No error
  }

  const routeIds = subcontractorAdjustmentPrices.map((item) => item.routeId);
  const uniqueRouteIds = new Set(routeIds);

  if (routeIds.length !== uniqueRouteIds.size) {
    return "Routes must be unique.";
  }

  return undefined; // No error
};

const SubcontractorAdjustmentPriceCreate = (props: CreateProps) => {
  const param = GetParam();
  const schoolIdSelected = SchoolIdSelected();

  const breadcrumb = [
    {
      title: "Subcontractors",
      link: `/subcontractors/${param.get("subcontractorId")}/show`,
    },
    {
      title: "Financial",
      link: `/subcontractors/${param.get("subcontractorId")}/show/2`,
    },
    {
      title: `Create Subcontractor Adjustment Price`,
      link: "",
    },
  ];

  const transform = async (data: any) => {
    if (
      data.subcontractorAdjustmentPrices &&
      data.subcontractorAdjustmentPrices.length > 0
    ) {
      data.subcontractorAdjustmentPrices =
        data.subcontractorAdjustmentPrices.map((item: any) => {
          return {
            schoolId: data.schoolId,
            subcontractorId: data.subcontractorId,
            busScheduleId: data.busScheduleId,
            routeId: item.routeId,
            price: item.price
              ? Number(Math.round(Number(item.price) * 100.0))
              : 0,
          };
        });
    }

    delete data.busScheduleId;
    delete data.schoolId;
    delete data.subcontractorId;

    return data;
  };

  return (
    <>
      <CreateWrapper
        breadcrumb={breadcrumb}
        headerTitle="Create Subcontractor Adjustment Price"
        transform={transform}
        isHaveSchoolFilter
        {...props}
      >
        <SimpleForm
          toolbar={
            <CustomCreateToolbar
              directAction={`/subcontractors/${param.get(
                "subcontractorId",
              )}/show/2`}
            />
          }
        >
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">General Info</Typography>
            </Grid>

            <Grid item xs={5}>
              <ReferenceInput
                label="Subcontractor"
                source="subcontractorId"
                reference="subcontractors"
                validate={required()}
                disabled
              >
                <AutocompleteInput optionText="name" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={7}></Grid>

            <Grid item xs={5}>
              <SelectedSchoolInput
                schoolIdSelected={schoolIdSelected}
                disabled
              />
            </Grid>
            <Grid item xs={7}></Grid>

            <Grid item xs={5}>
              <CustomWaveInput isRequired />
            </Grid>
            <Grid item xs={7}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Adjustment Price Detail</Typography>
            </Grid>

            <Grid item xs={5}>
              <Alert severity="info">
                By default, the price entered is considered a positive value.
                <br/>
                If you want to create a negative value, please prefix
                the price with a minus sign (e.g., <strong>-100</strong>).
              </Alert>
            </Grid>
            <Grid item xs={7}></Grid>

            <Grid item xs={5}>
              <ArrayInput
                source="subcontractorAdjustmentPrices"
                validate={[required(), validateUniqueRouteId]}
              >
                <SimpleFormIterator>
                  <FormDataConsumer>
                    {({ getSource }) => {
                      return (
                        <CustomRouteInput
                          source={getSource("routeId")}
                          isRequired
                        />
                      );
                    }}
                  </FormDataConsumer>

                  <CustomNumberInput
                    label="Price ($)"
                    source="price"
                    isRequired
                  />

                  <FormDataConsumer>
                    {({ formData, scopedFormData }) => {
                      return (
                        <RoutePriceComponent
                          routeId={scopedFormData?.routeId}
                          subcontractorId={formData?.subcontractorId}
                          schoolId={formData?.schoolId}
                          price={Number(scopedFormData?.price)}
                        />
                      );
                    }}
                  </FormDataConsumer>
                </SimpleFormIterator>
              </ArrayInput>
            </Grid>
            <Grid item xs={7}></Grid>
          </SanitizedGrid>
        </SimpleForm>
      </CreateWrapper>
    </>
  );
};

export default SubcontractorAdjustmentPriceCreate;
